import type { PianoChart } from '@shared/pianoChart';
import { MANIA_LANE_COUNT, type ManiaLane } from '@shared/maniaNote';
import { LANE_GAP_PX } from './pianoRenderConstants';
import { withAlpha } from './pianoRenderColour';
import type { PianoLayout } from '../pianoPlayfieldGeometry';

/**
 * Renders the per-lane key labels at the foot of each lane, with a
 * brief bounce pulse on each new keypress. Labels are pushed in by
 * the session so a rebind shows the correct letter (defaults to
 * Q/W/E/R, but a player who bound D/F/J/K sees those instead). Owns
 * the per-lane press-pulse timers (0..1) so the pulse decays smoothly
 * across frames without the orchestrator threading it through.
 */
export class KeyLabelRenderer {
  /**
   * Per-lane press-pulse animation timer (0..1). Bumps to 1 on the
   * frame a lane transitions unpressed→pressed, decays to 0 over
   * ~170 ms so each new keypress gets a brief bounce on its label.
   */
  private lanePressPulse: number[] = [0, 0, 0, 0];
  /** Last frame's pressed-lane set, diffed against current to detect press onsets. */
  private prevPressedLanes: Set<ManiaLane> = new Set();

  reset(): void {
    this.lanePressPulse.fill(0);
    this.prevPressedLanes.clear();
  }

  /**
   * Bump each lane's press-pulse to 1 on unpressed→pressed
   * transitions, then decay all pulses at ~6/sec so a key tap
   * produces a brief ~170ms bounce on its label. Keys held down
   * pulse only on the initial press, not for the duration.
   */
  advance(pressedLanes: ReadonlySet<ManiaLane>, dt: number): void {
    for (let l = 0; l < MANIA_LANE_COUNT; l += 1) {
      const lane = l as ManiaLane;
      if (pressedLanes.has(lane) && !this.prevPressedLanes.has(lane)) {
        this.lanePressPulse[l] = 1;
      }
    }
    this.prevPressedLanes.clear();
    for (const lane of pressedLanes) {
      this.prevPressedLanes.add(lane);
    }
    if (dt > 0) {
      const decayRate = 6;
      for (let l = 0; l < this.lanePressPulse.length; l += 1) {
        this.lanePressPulse[l] = Math.max(0, this.lanePressPulse[l] - decayRate * dt);
      }
    }
  }

  draw(
    ctx: CanvasRenderingContext2D,
    layout: PianoLayout,
    height: number,
    palette: PianoChart['palette'],
    pressedLanes: ReadonlySet<ManiaLane>,
    keyLabels: Record<ManiaLane, string>,
    reducedMotion: boolean,
  ): void {
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const labelY = (layout.hitLineY + height) / 2;
    for (let l = 0; l < MANIA_LANE_COUNT; l += 1) {
      const lane = l as ManiaLane;
      const pulse = this.lanePressPulse[l];
      const pressed = pressedLanes.has(lane);
      const x = layout.lane0X + l * (layout.laneWidth + LANE_GAP_PX) + layout.laneWidth / 2;
      const scale = reducedMotion ? 1 : 1 + pulse * 0.35;
      const fontPx = Math.round(22 * scale);
      ctx.font = `bold ${fontPx}px system-ui, sans-serif`;
      ctx.fillStyle = pressed ? palette.player : withAlpha(palette.ui, 0.6);
      const label = keyLabels[lane];
      if (!reducedMotion && pulse > 0) {
        ctx.save();
        ctx.shadowColor = palette.player;
        ctx.shadowBlur = 6 + pulse * 14;
        ctx.fillText(label, x, labelY);
        ctx.restore();
      } else {
        ctx.fillText(label, x, labelY);
      }
    }
  }
}
